import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { ConnectedPosition } from '@angular/cdk/overlay';
import { FormControl, FormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { Observable, Subject, forkJoin, of } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, map, switchMap, takeUntil } from 'rxjs/operators';
import { Ability, subject } from '@casl/ability';
import { ProjectsService } from '../../../../../../core/projects.service';
import { projectDetails } from '../../../../../../core/projects.selectors';
import { appDataDetails } from '../../../../../../../../shared/core/shared.selectors';
import { CommonService } from '../../../../../../../../shared/services/common.service';
import { AuthService } from '../../../../../../../../shared/services';
import { RiskRegisterService } from '../risk-register.service';
import { Risk, RiskBand, RiskConfig, RiskHeatMap, RiskListQuery } from '../risk-register.models';
import { ListResponse } from '../risk-register.service';
import {
  BAND_MAP, RISK_STATUS_FILTERS, STATUS_MAP, bandOf, ownerName, serverErrorMessage, trendArrow,
} from '../risk-register.util';
import { RiskDetailPanelComponent } from '../risk-detail-panel/risk-detail-panel.component';
import { RiskFormComponent } from '../risk-form/risk-form.component';
import { RiskConfigDialogComponent } from '../risk-config-dialog/risk-config-dialog.component';
import { RiskImportDialogComponent } from '../risk-import-dialog/risk-import-dialog.component';
import { RiskExportDialogComponent } from '../risk-export-dialog/risk-export-dialog.component';
import { TabbedFilterGroup } from '../../tabbed-filter/tabbed-filter.component';

const PAGE_SIZE = 25;

/** Rendered height of the heat-map popover plus its 8px gap and a little air. */
const HEAT_POPOVER_SPACE = 340;

/** Columns the API sorts by (sort_by) that the toolbar's Sort pill offers. */
const SORT_COLUMNS: { label: string; value: string }[] = [
  { label: 'Score', value: 'score' },
  { label: 'ID', value: 'risk_no' },
  { label: 'Last update', value: 'last_update_at' },
];

/** A query param that should hold one value; a hand-edited URL may repeat it. */
const one = (v: unknown): string | null => ((Array.isArray(v) ? v[0] : v) as string) || null;

interface LoadResult { list?: ListResponse<Risk>; heat?: RiskHeatMap; error?: string; page: number }

/**
 * Risk register list (spec §5.3). Server-side paging/filter/sort; every filter
 * lives in the URL so a refresh and a deep link (?riskId=) keep state.
 */
@Component({
  selector: 'app-risk-register-list',
  templateUrl: './risk-register-list.component.html',
  styleUrls: ['./risk-register-list.component.scss'],
  standalone: false,
})
export class RiskRegisterListComponent implements OnInit, OnDestroy {
  displayedColumns = ['risk_no', 'score', 'description', 'areas', 'owner', 'last_update'];
  rows: Risk[] = [];
  totalRecords = 0;
  heat: RiskHeatMap | null = null;
  /** The toolbar heat-map popover. */
  heatOpen = false;
  /** Below the Risk map button, right edges aligned, 8px gap; above only when there's no room below. */
  readonly heatPositions: ConnectedPosition[] = [
    { originX: 'end', originY: 'bottom', overlayX: 'end', overlayY: 'top', offsetY: 8 },
    { originX: 'end', originY: 'top', overlayX: 'end', overlayY: 'bottom', offsetY: -8 },
  ];
  config: RiskConfig = { risk_areas: [], disciplines: [] };
  members: { id: string; name: string }[] = [];
  selectedRow: Risk | null = null;
  loading = false;
  loadError: string | null = null;
  projectId: string;
  projectName = '';
  canManage = false;
  canPropose = false;
  canImport = false;
  canConfigure = false;
  isContractor = false;

  statusFilters = RISK_STATUS_FILTERS;
  sortColumns = SORT_COLUMNS;
  /** Tabs of the "Filters (n)" button; rebuilt when members, config or the heat-map cell change. */
  filterGroups: TabbedFilterGroup[] = [];
  /** True when the URL names a sort; otherwise the list falls back to score desc and the pill reads "Sort:". */
  sortExplicit = false;
  private filterGroupsKey = '';
  bandMap = BAND_MAP;
  statusMap = STATUS_MAP;
  bands: RiskBand[] = ['critical', 'high', 'medium', 'low'];
  searchForm = new FormGroup({ searchText: new FormControl('') });

  /** Current filter state, mirrored from the URL. */
  filter: { status: string[]; owner: string | null; area: string | null; discipline: string | null; band: RiskBand | null;
    cell: { probability: number; impact: number } | null; internal: boolean; search: string; sort: string; dir: 'asc' | 'desc'; page: number } = {
      status: ['open'], owner: null, area: null, discipline: null, band: null, cell: null, internal: false, search: '', sort: 'score', dir: 'desc', page: 1,
    };

  private pendingDeepLinkId: string | null = null;
  /** Filter state the last fetch was started for; a riskId-only URL change doesn't refetch. */
  private fetchedSignature: string | null = null;
  private readonly fetch$ = new Subject<void>();
  private readonly onDestroy = new Subject<void>();

  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private store = inject(Store);
  private dialog = inject(MatDialog);
  private projectsService = inject(ProjectsService);
  private commonService = inject(CommonService);
  private authService = inject(AuthService);
  private riskService = inject(RiskRegisterService);
  private ability = inject(Ability);

  ngOnInit(): void {
    this.projectId = this.route.snapshot.paramMap.get('projectId');
    this.refreshAbilities();
    this.store.select(appDataDetails).pipe(takeUntil(this.onDestroy)).subscribe(() => this.refreshAbilities());
    this.store.select(projectDetails).pipe(takeUntil(this.onDestroy)).subscribe((p: { name?: string } | null) => { if (p) this.projectName = p.name || ''; });

    const me = this.authService.getCurrentUser();
    const role = me?.roles?.find((r: { project_id: string; role: string }) => r.project_id === this.projectId)?.role || me?.role || '';
    this.isContractor = role.startsWith('contractor');

    this.riskService.config(this.projectId).pipe(takeUntil(this.onDestroy)).subscribe(cfg => { this.config = cfg; this.buildFilterGroups(); });
    this.projectsService.fetchProjectAccounts({ projectId: this.projectId, qp: { start: 0, total: 500 } })
      .pipe(takeUntil(this.onDestroy))
      .subscribe((resp: { data?: { id: string; first_name: string; last_name: string }[] }) => {
        this.members = (resp?.data || []).map(a => ({ id: a.id, name: `${a.first_name} ${a.last_name}`.trim() }))
          .sort((a, b) => a.name.localeCompare(b.name));
        this.buildFilterGroups();
      });

    // switchMap drops an in-flight load when a newer one starts, so a slow earlier
    // response can't overwrite newer rows / heat map.
    this.fetch$.pipe(switchMap(() => this.load()), takeUntil(this.onDestroy)).subscribe(result => this.applyLoad(result));

    this.route.queryParams.pipe(takeUntil(this.onDestroy)).subscribe(qp => {
      this.readFilter(qp);
      this.pendingDeepLinkId = qp.riskId || null;
      if (JSON.stringify(this.filter) !== this.fetchedSignature) this.fetch();
      else if (!this.loading) this.tryOpenDeepLink();
    });

    this.searchForm.get('searchText').valueChanges
      .pipe(debounceTime(300), distinctUntilChanged(), takeUntil(this.onDestroy))
      .subscribe(text => this.setParams({ search: text || null, page: null }));

    this.riskService.refresh$.pipe(takeUntil(this.onDestroy)).subscribe(() => this.fetch());
    this.commonService.clearRowSelection.pipe(takeUntil(this.onDestroy)).subscribe(() => (this.selectedRow = null));
  }

  private refreshAbilities(): void {
    if (!this.projectId) return;
    const s = subject('Risk', { project_id: this.projectId });
    this.canManage = this.ability.can('create', s);
    this.canPropose = this.ability.can('propose', s);
    this.canImport = this.ability.can('import', s);
    this.canConfigure = this.ability.can('configure', s);
  }

  private readFilter(qp: Params): void {
    const status = qp.status ? [].concat(qp.status) : ['open'];
    const cell = typeof qp.cell === 'string' && /^\d,\d$/.test(qp.cell)
      ? { probability: Number(qp.cell[0]), impact: Number(qp.cell[2]) } : null;
    this.filter = {
      status, owner: one(qp.owner), area: one(qp.area), discipline: one(qp.discipline),
      band: this.validBand(one(qp.band)), cell, internal: qp.internal === 'true', search: qp.search || '',
      sort: one(qp.sort) || 'score', dir: qp.dir === 'asc' ? 'asc' : 'desc', page: Math.max(1, Number(qp.page) || 1),
    };
    this.sortExplicit = !!one(qp.sort);
    this.buildFilterGroups();
    if (this.searchForm.get('searchText').value !== this.filter.search) {
      this.searchForm.get('searchText').setValue(this.filter.search, { emitEvent: false });
    }
  }

  setParams(params: Params): Promise<boolean> {
    return this.router.navigate([], { queryParams: params, queryParamsHandling: 'merge' });
  }

  setStatus(value: string): void {
    this.setParams({ status: value === 'all' ? 'all' : [value], page: null });
  }

  /** The status dropdown's value: the single status in the URL, or 'all'. */
  get statusValue(): string {
    return this.filter.status.length === 1 ? this.filter.status[0] : 'all';
  }

  get statusLabel(): string {
    return this.statusFilters.find(s => s.value === this.statusValue)?.label || 'All';
  }

  get sortLabel(): string | null {
    return this.sortExplicit ? this.sortColumns.find(c => c.value === this.filter.sort)?.label || this.filter.sort : null;
  }

  onSortColumnChange(column: string): void {
    this.setParams({ sort: column, dir: 'desc', page: null });
  }

  toggleSortDirection(): void {
    this.setParams({ sort: this.filter.sort, dir: this.filter.dir === 'desc' ? 'asc' : 'desc', page: null });
  }

  removeSort(event: Event): void {
    event?.stopPropagation();
    this.setParams({ sort: null, dir: null, page: null });
  }

  /** A hand-edited ?band= that isn't a known band is ignored (no chip, not sent to the API). */
  private validBand(value: string | null): RiskBand | null {
    return value && (this.bands as string[]).includes(value) ? (value as RiskBand) : null;
  }

  /**
   * Owner / Risk area / Discipline: the popup's only tabs -- each a single-value URL
   * param the API filters on. Band and heat-map cell are set by the KPI tiles and
   * the heat map and show as removable chips; "Internal only" is the popup's footer
   * checkbox (non-contractors). Both count toward "Filters (n)" via extraFilterCount.
   * Keep this at 3 tabs: with fitTabs, 3 fit the 320px popup with about 17px spare
   * and the popup hides Material's pagination arrows, so a 4th needs a different layout.
   */
  private buildFilterGroups(): void {
    const key = JSON.stringify([this.members, this.config]);
    if (key === this.filterGroupsKey) return;
    this.filterGroupsKey = key;
    const opts = (items: { label: string; value: string }[]) => items.map(i => ({ ...i, isChecked: false }));
    this.filterGroups = [
      { label: 'Owner', queryParam: 'owner', single: true, searchable: true, testId: 'filter-owner',
        options: opts(this.members.map(m => ({ label: m.name, value: m.id }))) },
      { label: 'Risk area', queryParam: 'area', single: true, searchable: true, testId: 'filter-area',
        options: opts((this.config.risk_areas || []).map(a => ({ label: a, value: a }))) },
      { label: 'Discipline', queryParam: 'discipline', single: true, testId: 'filter-discipline',
        options: opts((this.config.disciplines || []).map(d => ({ label: d, value: d }))) },
    ];
  }

  /** Active filters outside the popup's tabs: band chip, cell chip, Internal only. */
  get extraFilterCount(): number {
    const f = this.filter;
    return (f.band ? 1 : 0) + (f.cell ? 1 : 0) + (f.internal && !this.isContractor ? 1 : 0);
  }

  get bandChipLabel(): string | null {
    return this.filter.band ? this.bandMap[this.filter.band]?.label ?? null : null;
  }

  removeBandChip(): void {
    this.setParams({ band: null, page: null });
  }

  removeCellChip(): void {
    this.onCell(null);
  }

  setInternal(checked: boolean): void {
    this.setParams({ internal: checked ? 'true' : null, page: null });
  }

  /**
   * Opens / closes the heat-map popover. The toolbar usually sits low in the first
   * screen, so when the popover wouldn't fit below the button the page scrolls just
   * enough to make room (it only falls back to opening above if the page can't scroll).
   */
  toggleHeat(trigger: HTMLElement): void {
    if (this.heatOpen) { this.heatOpen = false; return; }
    const shortBy = trigger.getBoundingClientRect().bottom + HEAT_POPOVER_SPACE - window.innerHeight;
    if (shortBy > 0) window.scrollBy({ top: shortBy });
    this.heatOpen = true;
  }

  onHeatKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape') this.heatOpen = false;
  }

  onCell(cell: { probability: number; impact: number } | null): void {
    this.setParams({ cell: cell ? `${cell.probability},${cell.impact}` : null, page: null });
  }

  setSort(column: string): void {
    const dir = this.filter.sort === column && this.filter.dir === 'desc' ? 'asc' : 'desc';
    this.setParams({ sort: column, dir, page: null });
  }

  onPageChanged(page: number): void {
    // One navigation that drops riskId with the page change. Closing the panel first
    // raced it: the close navigation was superseded, riskId survived and the
    // deep-link handler re-opened the panel on the new page.
    this.setParams({ page: page > 1 ? page : null, riskId: null }).then(() => this.projectsService.closeRightPanel.next());
  }

  fetch(): void {
    if (!this.projectId) return;
    this.fetchedSignature = JSON.stringify(this.filter);
    this.fetch$.next();
  }

  private load(): Observable<LoadResult> {
    this.loading = true;
    this.loadError = null;
    const f = this.filter;
    const qp: RiskListQuery = {
      start: (f.page - 1) * PAGE_SIZE, total: PAGE_SIZE, status: f.status,
      owner: f.owner || undefined, risk_area: f.area || undefined, discipline: f.discipline || undefined,
      band: f.band || undefined, probability: f.cell?.probability, impact: f.cell?.impact,
      internal: f.internal ? 'true' : undefined, search: f.search || undefined,
      sort_by: f.sort as RiskListQuery['sort_by'], sort_order: f.dir,
    };
    return forkJoin({ list: this.riskService.list(this.projectId, qp), heat: this.riskService.heatMap(this.projectId) }).pipe(
      map(({ list, heat }): LoadResult => ({ list, heat, page: f.page })),
      catchError(err => of<LoadResult>({ error: serverErrorMessage(err?.error, 'Failed to load the risk register'), page: f.page })),
    );
  }

  private applyLoad(result: LoadResult): void {
    this.loading = false;
    if (result.error) {
      this.loadError = result.error;
      this.rows = [];
      this.totalRecords = 0;
      // A retry with the same filters (e.g. refresh$) must not be skipped.
      this.fetchedSignature = null;
      return;
    }
    this.rows = result.list?.data || [];
    this.totalRecords = result.list?.meta?.totalRecords || this.rows.length;
    this.heat = result.heat;
    if (this.selectedRow) this.selectedRow = this.rows.find(r => r.id === this.selectedRow.id) || this.selectedRow;
    // app-paginator resets itself to page 1 whenever totalItems changes; put it
    // back on the URL's page once that change has rendered (deep link ?page=3).
    setTimeout(() => this.projectsService.changeCurrentPageNumber.next(result.page));
    this.tryOpenDeepLink();
  }

  private tryOpenDeepLink(): void {
    if (!this.pendingDeepLinkId) return;
    const id = this.pendingDeepLinkId;
    this.pendingDeepLinkId = null;
    if (this.selectedRow?.id === id) return;
    const row = this.rows.find(r => r.id === id);
    if (row) { this.openRow(row); return; }
    this.riskService.get(id).pipe(takeUntil(this.onDestroy)).subscribe({
      next: risk => this.openRow(risk),
      // Deleted, or internal and hidden from this user: drop the stale deep link.
      error: () => this.setParams({ riskId: null }),
    });
  }

  band(risk: Risk): RiskBand { return bandOf(risk.score); }
  trend(risk: Risk) { return trendArrow(risk.latest_update); }
  owner(risk: Risk): string { return ownerName(risk); }
  isTruncated(el: HTMLElement): boolean { return el.scrollWidth > el.clientWidth; }

  lastUpdate(risk: Risk): string | null {
    const lu = risk.latest_update;
    return lu && ['update', 'score_changed'].includes(lu.type) ? lu.created_at : null;
  }

  get hasFilters(): boolean {
    const f = this.filter;
    return !!(f.search || f.owner || f.area || f.discipline || f.band || f.cell || f.internal);
  }

  rowClicked(row: Risk): void {
    this.openRow(row);
  }

  private openRow(row: Risk): void {
    this.selectedRow = row;
    this.projectsService.openRightPanel.next({ component: RiskDetailPanelComponent, data: { riskId: row.id, projectId: this.projectId } });
    this.setParams({ riskId: row.id });
  }

  create(): void {
    this.selectedRow = null;
    this.setParams({ riskId: null });
    this.projectsService.openRightPanel.next({
      component: RiskFormComponent,
      data: { projectId: this.projectId, mode: this.canManage ? 'create' : 'propose', config: this.config, members: this.members },
    });
  }

  openImport(): void {
    this.dialog.open(RiskImportDialogComponent, {
      data: { projectId: this.projectId }, panelClass: 'floating-dialog-box', maxHeight: '90vh', width: '560px', disableClose: true,
    }).afterClosed().subscribe(done => { if (done) this.riskService.refresh$.next(); });
  }

  openExport(): void {
    this.dialog.open(RiskExportDialogComponent, {
      data: { projectId: this.projectId, projectName: this.projectName, isContractor: this.isContractor },
      panelClass: 'floating-dialog-box', maxHeight: '90vh', width: '460px',
    });
  }

  openConfig(): void {
    this.dialog.open(RiskConfigDialogComponent, {
      data: { projectId: this.projectId, config: this.config }, panelClass: 'floating-dialog-box', maxHeight: '90vh', width: '560px',
    }).afterClosed().subscribe((cfg: RiskConfig | undefined) => { if (cfg) this.config = cfg; });
  }

  clearSearch(): void { this.searchForm.get('searchText').setValue(''); }

  ngOnDestroy(): void { this.onDestroy.next(); this.onDestroy.complete(); }
}
